import React from 'react';
import { Box, Paper, Stack, Typography, alpha } from '@mui/material';
import HubRounded from '@mui/icons-material/HubRounded';
import { fmtMetricValue, fmtPercent } from './formatters';
import { STATUS_COLORS } from './StatusChip';
import type { ChainPivotPanel } from './types';

export interface CrossPivotPanelProps {
  panel: ChainPivotPanel;
  currencySymbol?: string;
}

/** Generic "is this factor affecting one entity, or everyone who touches it?"
 * pivot view — e.g. Broker Monitor's operator-slowdown cross-broker check.
 * Renders a headline + per-row breakdown; carries no domain knowledge of
 * what the rows represent. */
export default function CrossPivotPanel({ panel, currencySymbol = '$' }: CrossPivotPanelProps) {
  return (
    <Paper variant="outlined" sx={(t) => ({ borderRadius: `${t.ds?.radius?.md ?? 8}px`, overflow: 'hidden' })}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={(t) => ({ px: 2, py: 1.25, borderBottom: `1px solid ${t.tokens.border.subtle}`, bgcolor: t.tokens.surface.sunken })}
      >
        <HubRounded sx={{ fontSize: 18, color: 'info.main' }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {panel.pivot_name}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" fontWeight={700} color={panel.is_widespread ? 'error.main' : 'text.secondary'}>
          {panel.affected_count}/{panel.total_count} affected
        </Typography>
      </Stack>
      <Box sx={{ p: 2.5 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
          {panel.headline}
        </Typography>
        <Stack spacing={0.5}>
          {panel.rows.map((row) => (
            <Stack
              key={row.key}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={(t) => ({
                px: 1,
                py: 0.6,
                borderRadius: `${t.ds?.radius?.sm ?? 6}px`,
                bgcolor: alpha(STATUS_COLORS[row.status], 0.06),
              })}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: STATUS_COLORS[row.status], flexShrink: 0 }} />
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtMetricValue(row, currencySymbol)}
              </Typography>
              {row.pct_change != null && (
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ minWidth: 52, textAlign: 'right' }}
                  color={row.status === 'red' ? 'error.main' : row.status === 'yellow' ? 'warning.main' : 'text.secondary'}
                >
                  {fmtPercent(row.pct_change, true)}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
        {panel.method_note && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5, lineHeight: 1.5 }}>
            {panel.method_note}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
